import { addMonths, lastDayOfMonth } from "date-fns";
import type { DateRange } from "models/formTypes/util";
import type { WintSuperkollAPIModel } from "wint-api-client/dist/types";

type GenerateRequest = WintSuperkollAPIModel.GenerateMonthlyReportRequest;
type CompleteRange = { start: Date; end: Date };

// Core accepts one month or three in a row; any other count answers 400.
const ALLOWED_MONTH_COUNTS = [1, 3];

const monthIndex = (year: number, month: number) => year * 12 + month;

const countMonths = (start: Date, end: Date): number =>
  monthIndex(end.getFullYear(), end.getMonth()) - monthIndex(start.getFullYear(), start.getMonth()) + 1;

const isReportableRange = (range?: DateRange): range is CompleteRange =>
  !!range?.start && !!range?.end && ALLOWED_MONTH_COUNTS.includes(countMonths(range.start, range.end));

/**
 * The API names a period by its last calendar month plus a month count, not by two dates: a date
 * carries a time and an offset, and serialising a local midnight could land in the month before.
 */
const toGenerateRequest = (range: CompleteRange): GenerateRequest => ({
  periodYear: range.end.getFullYear(),
  periodMonth: range.end.getMonth() + 1,
  periodMonthCount: countMonths(range.start, range.end),
});

/**
 * A new From keeps To only when the two still make a reportable period. Otherwise To becomes From's
 * month, so a one-month report takes a single pick and the range is never left invalid by From alone.
 */
const withStart = (range: DateRange, start?: Date): DateRange => ({
  start,
  end: start && !isReportableRange({ start, end: range.end }) ? lastDayOfMonth(start) : range.end,
});

/**
 * The To months that make a reportable period with From, as picker limits: anything outside them
 * shows disabled like the months that are not completed yet.
 */
const toMonthLimits = (start: Date | undefined, lastAllowed: Date) => {
  if (!start) return { minDate: undefined, maxDate: lastAllowed, excludeMonths: [] };

  const longest = Math.max(...ALLOWED_MONTH_COUNTS);
  const lastOfLongest = lastDayOfMonth(addMonths(start, longest - 1));
  const excludeMonths = Array.from({ length: longest }, (_, offset) => offset)
    .filter((offset) => !ALLOWED_MONTH_COUNTS.includes(offset + 1))
    .map((offset) => addMonths(start, offset));

  return {
    minDate: start,
    maxDate: lastOfLongest < lastAllowed ? lastOfLongest : lastAllowed,
    excludeMonths,
  };
};

/**
 * The generate request that reproduces a stored report's own period. Takes `yyyy-MM-dd` strings so
 * the caller decides how a stored date becomes a calendar day, rather than this reading local
 * getters off a Date that may have crossed a timezone.
 */
const toRetryRequest = (periodStartIso: string, periodEndIso: string): GenerateRequest => {
  const [startYear, startMonth] = periodStartIso.split("-").map(Number);
  const [periodYear, periodMonth] = periodEndIso.split("-").map(Number);
  return {
    periodYear,
    periodMonth,
    periodMonthCount: monthIndex(periodYear, periodMonth) - monthIndex(startYear, startMonth) + 1,
  };
};

export { countMonths, isReportableRange, toGenerateRequest, withStart, toMonthLimits, toRetryRequest };
